import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { haptic } from '../../lib/tma';
import type { HapticKind } from '../../lib/tma';

type Variant = 'primary' | 'ink' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  hapticKind?: HapticKind | false;
}

const baseClasses =
  'inline-flex items-center justify-center gap-2 font-semibold ' +
  'rounded-full select-none ' +
  'transition-[transform,opacity,background-color] duration-150 ease-smooth ' +
  'active:scale-[0.97] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

/** `primary` is the lime main action: at most one per screen. `ink` is the everyday dark button. */
const variants: Record<Variant, string> = {
  primary: 'bg-accent text-accent-on',
  ink: 'bg-ink text-ink-on',
  secondary: 'bg-surface-2 text-fg',
  ghost: 'bg-transparent text-fg active:bg-surface-2',
  destructive: 'bg-destructive-solid text-destructive-on',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'ink',
    size = 'md',
    fullWidth = false,
    loading = false,
    leftIcon,
    rightIcon,
    hapticKind,
    className,
    children,
    disabled,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;

  const defaultHaptic: HapticKind =
    variant === 'destructive'
      ? 'warning'
      : variant === 'primary' || variant === 'ink'
        ? 'medium'
        : 'light';
  const hk = hapticKind === undefined ? defaultHaptic : hapticKind;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      onClick={(e) => {
        if (isDisabled) return;
        if (hk) haptic(hk);
        onClick?.(e);
      }}
      className={cn(baseClasses, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {leftIcon}
          {children}
          {rightIcon}
        </>
      )}
    </button>
  );
});

function Spinner() {
  return (
    <span
      className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
      aria-label="Загрузка"
    />
  );
}
